import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message } from 'antd';

import RecordsHOC from '../RecordsHOC';
import LoadingDialog from '../../../components/header/loadingDialog';
import SendRecordsDialog from '../../../components/sent/sendRecordsDialog';
import { uploadedColumns } from '../shared/constants';
import UploadedActionBar from './UploadedRecordsRedesign/UploadedActionBar';
import UploadedFilterBar from './UploadedRecordsRedesign/UploadedFilterBar';
import {
  acquireRecordsRedesignBodyClass,
  releaseRecordsRedesignBodyClass,
} from './shared/RecordsRedesign/useRecordsRedesignBodyClass';
import './shared/RecordsRedesign/recordsRedesign.scss';
import './UploadedRecordsRedesign/uploadedRecordsRedesign.scss';

import {
  getUploadedRecordsByPage,
  getUploadedByQuery,
  mergeRecords,
  duplicateRecord,
  archiveRecords,
  unarchiveRecords,
  triggerAutoUpload,
} from '../../../actions/records';
import {
  archiveFailed,
  unarchiveFailed,
  ARCHIVE_FAILURE_MESSAGE,
  UNARCHIVE_FAILURE_MESSAGE,
} from '../../../util/archiveOutcome';
import {
  sendPatientRecord,
  sendToMultipleRecipients,
  sendToDistributionList,
  sendBulkRecords,
} from '../../../actions/faxes';
import searchRecipient from '../../../actions/search';
import { closeSendDialog } from '../../../actions/appActions';
import { openModal } from '../../../actions/modal';
import {
  hasFeatureInLocalStorage,
  verifyFeatureAccess,
  INBOX_BACKGROUND_REFRESH_FEATURE,
} from '../../../global/featureFlags';
import { formatDate } from '../../../global/validation';
import {
  buildCreateTaskModalData,
  handleDirectAutoUpload,
} from '../../../components/chartActions/helpers';
import {
  canUseStandaloneTask,
  fetchStandaloneTaskSupport,
} from '../../../components/chartActions/credentialCapabilities';

const MAX_BULK_SEND_RECORDS = 50;

class UploadedRecords extends Component {
  static propTypes = {
    // Redux actions
    sendPatientRecord: PropTypes.func.isRequired,
    sendToMultipleRecipients: PropTypes.func.isRequired,
    sendBulkRecords: PropTypes.func.isRequired,
    closeSendDialog: PropTypes.func.isRequired,
    searchRecipient: PropTypes.func.isRequired,
    mergeRecords: PropTypes.func.isRequired,
    duplicateRecord: PropTypes.func.isRequired,
    archiveRecords: PropTypes.func.isRequired,

    // Stores the array of results from search autocomplete
    dataSource: PropTypes.array.isRequired,
    openModal: PropTypes.func.isRequired,

    // Data from Redux store
    /* Send records dialog visible */
    // This is kept globally to close and allow the <LoadingDialog>
    // to take its place
    loadingDialogOpen: PropTypes.bool.isRequired,

    /* Whether a fax is currently in transmission from <SendRecordsDialog /> */
    faxSending: PropTypes.bool.isRequired,

    // Checks whether autocomplete search is request is still loading
    isLoading: PropTypes.bool.isRequired,

    /* Error message encountered while sending a record */
    faxErrorMessage: PropTypes.string,

    // Checks whether an error was encountered while sending a record
    faxError: PropTypes.bool,

    /* Data selected on inbox page */
    selectedData: PropTypes.arrayOf(PropTypes.shape({
      file_token: PropTypes.string,
      patient_dob: PropTypes.string,
      patient_name: PropTypes.string,
      patient_first_name: PropTypes.string,
      patient_middle_name: PropTypes.string,
      patient_last_name: PropTypes.string,
      patient_phone_number: PropTypes.string,
      upload_date: PropTypes.string,
      privacy_level: PropTypes.string,
      document_title: PropTypes.string,
      document_category: PropTypes.string,
      document_categories: PropTypes.array,
      chart_filed: PropTypes.bool,
    })),

    defaultFaxNumber: PropTypes.string,
    associatedFaxNumbers: PropTypes.array.isRequired,
    // Checks whether user can record without access code sheet
    userCanSendFaxRecordsOnly: PropTypes.bool.isRequired,
    fileToken: PropTypes.string,
    currentOrganizationId: PropTypes.string,
    autoUploadEmr: PropTypes.string,
    hasEmrIntegrationCredential: PropTypes.bool.isRequired,
    referralManagement: PropTypes.bool,
    paramTools: PropTypes.object,
    getPageDataWithQuery: PropTypes.func,
    location: PropTypes.object,
  }

  static defaultProps = {
    selectedData: [],
    faxError: false,
    faxErrorMessage: null,
    defaultFaxNumber: null,
    fileToken: '',
    referralManagement: false,
    autoUploadEmr: '',
    paramTools: {},
    getPageDataWithQuery: () => {},
    location: {},
  }

  constructor(props) {
    super(props);
    this.state = {
      sendDialogOpen: false,
      autoUploadToEMR: false,
      createPatientChart: false,
      hideTaskButton: false,
      taskCreation: false,
      credentialSupportsStandaloneTask: !props.hasEmrIntegrationCredential,
    };
  }

  componentDidMount = async () => {
    acquireRecordsRedesignBodyClass();
    try {
      const autoUploadToEMR = await verifyFeatureAccess('manual_upload_emr');
      const createPatientChart = await verifyFeatureAccess('create_patient_chart');
      const hideTaskButton = await verifyFeatureAccess('hide_task_creation_button');
      const taskCreation = await verifyFeatureAccess('task_creation');
      const credentialSupportsStandaloneTask = this.props.hasEmrIntegrationCredential
        ? await fetchStandaloneTaskSupport()
        : true;
      this.setState({
        autoUploadToEMR,
        createPatientChart,
        hideTaskButton,
        taskCreation,
        credentialSupportsStandaloneTask,
      });
    } catch (error) {
      // Feature flag service error - default to hiding chart buttons (safer than showing)
      console.error('Failed to load feature flags:', error);
      message.error('Unable to load chart action permissions. Please refresh the page.');
      this.setState({
        autoUploadToEMR: false,
        createPatientChart: false,
        hideTaskButton: true,
        taskCreation: false,
      });
    }
  };

  componentWillUnmount() {
    releaseRecordsRedesignBodyClass();
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ errorMessage: nextProps.faxErrorMessage });
  }

  /**
   * Hide/show the <SendRecordsDialog>
   * @param  {Boolean} bool whether to hide or show
   * @return {Void}
   */
  sendDialogVisible = (bool) => {
    this.setState({ sendDialogOpen: bool });
  };

  /**
   * When the Send button is clicked, check if the patient name and dob isn't present.
   * The Edit Patient dialog will be opened if either is missing (single record only)
   * The send dialog will be opened if both are present or for multiple records
   */
  checkPatientDetails = async () => {
    const { selectedData } = this.props;
    const isMultiSelect = selectedData.length > 1;

    // For multiple records, skip the patient info check and open send dialog directly
    if (isMultiSelect) {
      const canSend = await verifyFeatureAccess('send_record');
      if (canSend) {
        this.setState({ sendDialogOpen: true });
      } else {
        const modalType = {
          type: 'RAISE_FLAG',
          featureFlag: 'send_record',
        };
        this.props.openModal(modalType);
      }
      return;
    }

    // Single record logic
    const {
      file_token,
      patient_name,
      patient_dob,
      patient_phone_number,
      privacy_level,
      can_edit_patient_info,
      can_edit_privacy_level,
      document_title,
      document_category,
      document_categories,
    } = selectedData[0];
    const canSend = await verifyFeatureAccess('send_record');
    let titleText;
    if (!document_title && !patient_name) {
      titleText = 'patient name or document title';
    }

    const patient = {
      file_token,
      patient_name,
      patient_dob,
      patient_phone_number,
      privacy_level,
      can_edit_patient_info,
      can_edit_privacy_level,
      document_title,
      document_category,
      document_categories,
    };

    if (titleText) {
      const modalType = {
        type: 'EDIT_PATIENT',
        data: {
          patient,
          showSendDialog: this.sendDialogVisible,
          title: `To send this record please update the ${titleText}.`,
          buttonText: 'Continue',
          fromPage: 'uploaded',
        },
      };
      this.props.openModal(modalType);
    } else if (canSend) {
      this.setState({ sendDialogOpen: true });
    } else {
      const modalType = {
        type: 'RAISE_FLAG',
        featureFlag: 'send_record',
      };
      this.props.openModal(modalType);
    }
  }

  editPatientOpen = () => {
    const selectedData = this.props.selectedData[0];
    const modalType = {
      type: 'EDIT_PATIENT',
      data: {
        patient: selectedData,
        fromPage: 'uploaded',
      },
    };
    this.props.openModal(modalType);
  };

  openUploadModal = async () => {
    const canUpload = await verifyFeatureAccess('upload_record');
    const modal = canUpload ? 'UPLOAD_RECORD' : 'RAISE_FLAG';
    const modalType = {
      type: modal,
      ...(!canUpload && { featureFlag: 'upload_record' }),
    };
    this.props.openModal(modalType);
  }

  handleMergeRecords = async (tokens) => {
    const canMergeRecords = await verifyFeatureAccess('merge_record');
    if (canMergeRecords) return this.props.mergeRecords(tokens);
    const modalType = {
      type: 'RAISE_FLAG',
      featureFlag: 'merge_record',
    };
    this.props.openModal(modalType);
  }

  handleMergeAndArchiveRecords = async (tokens) => {
    const mergeResponse = await this.handleMergeRecords(tokens);
    if (mergeResponse && !mergeResponse.error) {
      this.archiveRecord(tokens);
    }
  }

  cloneSelectedRecord = async () => {
    const {
      selectedData,
      duplicateRecord: cloneRecord,
    } = this.props;
    await cloneRecord(selectedData[0].file_token);

    const { fileToken } = this.props;
    if (fileToken !== '') {
      // If we got a new file token for the copied file, let's open it in the viewer.
      window.open(`${window.location.origin}/app/view/${fileToken}?from=uploaded`, '_self');
      message.success({
        // eslint-disable-next-line quotes
        content: "You've successfully copied the record.",
      });
    } else {
      // There was a problem copying the file, let's display the error.
      message.error({
        content: 'There was an issue copying the record, please try again or contact Medsender support.',
      });
    }
  }

  /**
   * If <SendRecordsDialog> data is valid, transmit the record via
   * email or fax, as selected by the user
   * @param  {String} org     organization name for a fax
   * @param  {String} contact organization contact for a fax
   * @param  {String} email   email address of Medsender user if sending thru
   *                          medsender
   * @param  {Boolean} canSend whether to allow the user to send the record
   *                           to others when sending thru Medsender
   * @return {Void}
   */
  handleSendDialogSubmit = async (org, contact, callerId, canSend, emailValid, isPatient, patient, documentType, memo, subject, signatureRequired, removeCoversheet, multipleProviderRecipients, allowPatientEdits, disableSecureReplies, scheduledDateTime, distributionListSlug = null) => {
    const { selectedData } = this.props;
    const isMultiSelect = selectedData.length > 1;
    const recipients = multipleProviderRecipients.map(recipientObj => recipientObj.recipient);

    if (isMultiSelect) {
      // Bulk send mode - send multiple records to the same recipients
      const recordIds = selectedData.map(record => record.file_token);
      await this.props.sendBulkRecords(
        recordIds,
        recipients,
        canSend,
        documentType,
        subject,
        memo,
        signatureRequired,
        callerId,
        allowPatientEdits,
        removeCoversheet,
        scheduledDateTime,
        distributionListSlug,
        org,
        contact
      );

      // Close dialog after sending
      await this.sendDialogVisible(false);
      const { faxError, faxErrorMessage, bulkSendResponse } = this.props;

      if (!faxError) {
        const queuedCount = bulkSendResponse?.queued?.length ?? recordIds.length;
        const failedCount = bulkSendResponse?.failed?.length || 0;

        if (failedCount > 0) {
          message.warning({
            content: `${queuedCount} record(s) queued for sending. ${failedCount} record(s) failed.`,
            duration: 5,
          });
        } else {
          message.success({
            content: `${queuedCount} record(s) have been queued and will be sent shortly.`,
          });
        }
      } else {
        message.error({
          content: faxErrorMessage || 'Failed to send records.',
        });
      }
      return;
    }

    // Single record send logic
    const recordId = selectedData.file_token || selectedData[0].file_token;

    if (emailValid && !isPatient && multipleProviderRecipients.length >= 1) {
      await this.props.sendToMultipleRecipients(recordId, recipients, canSend, documentType, subject, memo, signatureRequired, callerId, allowPatientEdits, org, contact, false, scheduledDateTime);
    } else if (isPatient) {
      await this.props.sendPatientRecord(recordId, patient, memo, disableSecureReplies);
    } else if (distributionListSlug) {
      await this.props.sendToDistributionList(recordId, distributionListSlug.slug, memo, documentType, subject, signatureRequired, callerId, allowPatientEdits, removeCoversheet, scheduledDateTime);
    } else {
      await this.props.sendToMultipleRecipients(recordId, recipients, canSend, documentType, subject, memo, signatureRequired, callerId, allowPatientEdits, org, contact, removeCoversheet, scheduledDateTime);
    }

    // Close dialog after sending record
    await this.sendDialogVisible(false);
    const { faxError, faxErrorMessage } = this.props;
    if (!faxError) {
      message.success({
        content: 'Selected record(s) have been queued and will be sent shortly.',
      });
    } else {
      message.error({
        content: faxErrorMessage,
      });
    }

    const dateHasBeenChanged = formatDate(selectedData[0].patient_dob) !== formatDate(patient.dob);
    const nameHasBeenChanged = selectedData[0].patient_name !== patient.name;
    if (dateHasBeenChanged || nameHasBeenChanged) {
      setTimeout(() => { window.location.reload(); }, 800);
    }
  };

  // Archives immediately, without asking. The ARCHIVE_RECORD confirmation modal is
  // now only on the full-page viewer (/app/view/:id): archiving from a list is a
  // high-volume action, and the prompt was pure friction there. Deliberately shaped
  // like unarchiveRecordOpen below, which has never asked.
  //
  // Failure is read from the resolved result rather than from
  // `this.props.archiveErrorMessage` after the await — see util/archiveOutcome.
  archiveRecord = async (tokens) => {
    const { archiveRecords: archive } = this.props;
    const result = await archive(tokens);

    if (archiveFailed(result)) {
      message.error({ content: ARCHIVE_FAILURE_MESSAGE });
      return;
    }
    message.success({ content: 'Successfully archived selected record(s)' });
    // With inbox_background_refresh on, the archive action requests a background
    // refresh of the inbox table (REQUEST_INBOX_TABLE_REFRESH), so no page reload
    // is needed. Off, keep the legacy reload.
    if (!hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)) {
      window.location.reload();
    }
  }

  // Failure comes from the resolved result, as with archiveRecord above. Re-reading
  // `this.props.archiveErrorMessage` after the await did get fresh props, which is
  // why this looked correct — but no unarchive action ever writes that field (only
  // the ARCHIVE_* cases do), so it was always '' and this always reported success.
  unarchiveRecordOpen = async (tokens) => {
    const { unarchiveRecords } = this.props;
    const result = await unarchiveRecords(tokens);

    if (unarchiveFailed(result)) {
      message.error({ content: UNARCHIVE_FAILURE_MESSAGE });
      // Deliberately no reload on failure, as in ViewerToolbar/ArchiveButton: nothing
      // changed server-side so the list on screen is still accurate, and reloading
      // would refresh away the error toast that is the only evidence of the failure.
      return;
    }
    message.success({ content: 'Successfully unarchived selected record(s)' });
    // With inbox_background_refresh on, the unarchive action requests a
    // background refresh of the inbox table (REQUEST_INBOX_TABLE_REFRESH), so no
    // page reload is needed. Off, keep the legacy reload.
    if (!hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)) {
      window.location.reload();
    }
  }

  autoUploadRecord = async (selectedRecord) => {
    const {
      patient_name: patientName,
      patient_dob: patientDOB,
      send_token: sendToken,
      document_title: documentTitle,
      document_category: documentCategory,
      practitioner_name: practitionerName,
      patient_first_name: patientFirstName,
      patient_middle_name: patientMiddleName,
      patient_last_name: patientLastName,
      upload_date: uploadDate,
      api_credentials: apiCredentials,
    } = selectedRecord;
    const { autoUploadEmr, currentOrganizationId, openModal } = this.props;

    // If EMR is EMA, allow modal to open as Patient MRN is an option
    if (autoUploadEmr !== 'EMA' && (!patientName || !patientDOB)) {
      message.error({
        content: 'Cannot upload a document without a valid name and date of birth. Edit record details and try again.',
      });
      return;
    }

    const fileNameSupported = [
      'Kareo',
      'OncoEMR',
      'Nymbl',
      'EMA',
      'AllegianceMD',
      'Elation',
    ];

    // Build default file name for uploaded records
    let recordFileName = documentTitle;
    if (!recordFileName) {
      // Sanitize date to avoid slashes that could be interpreted as path separators
      // Format: 2026-02-17_uploaded_record instead of 02/17/2026_12:00_AM_uploaded_record
      const sanitizedDate = uploadDate?.replace(/\//g, '-').replace(/ /g, '_') || 'unknown-date';
      recordFileName = `${sanitizedDate}_uploaded_record`;
    }

    if (
      fileNameSupported.includes(autoUploadEmr)
      || (autoUploadEmr === 'ECW' && currentOrganizationId > 4206)
      || (autoUploadEmr === 'CareTracker' && currentOrganizationId === 4193)
    ) {
      const modalType = {
        type: 'AUTO_UPLOAD',
        data: {
          fileName: recordFileName,
          providerName: practitionerName,
          dateOfService: selectedRecord.date_of_service,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          patientDOB,
          documentCategory,
          sendToken,
          receiverType: 'uploaded_record',
          autoUploadEmr,
          apiCredentials,
          currentOrganizationId,
        },
      };
      openModal(modalType);
    } else if (autoUploadEmr === 'AdvancedMD') {
      const modalType = {
        type: 'AUTO_UPLOAD',
        data: {
          fileName: recordFileName,
          providerName: practitionerName,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          patientDOB,
          documentCategory,
          sendToken,
          autoUploadEmr,
          receiverType: 'uploaded_record',
          apiCredentials,
        },
      };
      openModal(modalType);
    } else if (autoUploadEmr === 'Brightree') {
      const modalType = {
        type: 'AUTO_UPLOAD',
        data: {
          fileName: recordFileName,
          patientName,
          patientFirstName,
          patientMiddleName,
          patientLastName,
          patientDOB,
          sendToken,
          autoUploadEmr,
          receiverType: 'uploaded_record',
          apiCredentials,
        },
      };
      openModal(modalType);
    } else {
      // Direct auto-upload without modal.
      handleDirectAutoUpload({
        triggerAutoUpload: this.props.triggerAutoUpload,
        sendToken,
        receiverType: 'uploaded_record',
      });
    }
  };

  // For orgs WITHOUT EMR Integration Credentials (legacy flow)
  newChart = async (selectedRecord) => {
    const { openModal } = this.props;

    // Build default file name — same logic as autoUploadRecord
    let recordFileName = selectedRecord.document_title;
    if (!recordFileName) {
      const sanitizedDate = selectedRecord.upload_date?.replace(/\//g, '-').replace(/ /g, '_') || 'unknown-date';
      recordFileName = `${sanitizedDate}_uploaded_record`;
    }

    const modalType = {
      type: 'CREATE_NEW_CHART',
      data: {
        name: selectedRecord.patient_name,
        patient_first_name: selectedRecord.patient_first_name,
        patient_middle_name: selectedRecord.patient_middle_name,
        patient_last_name: selectedRecord.patient_last_name,
        dob: selectedRecord.patient_dob,
        record: selectedRecord.send_token,
        fileName: recordFileName,
        practitionerName: selectedRecord.practitioner_name,
        receiverType: 'uploaded_record',
        receivedAt: selectedRecord.upload_date,
        documentCategory: selectedRecord.document_category,
        apiCredentials: selectedRecord.api_credentials,
      },
    };
    openModal(modalType);
  };

  moveToReferralManagement = () => {
    const { selectedData, openModal } = this.props;
    if (selectedData.length !== 1) return;

    const modalType = {
      type: 'MOVE_TO_REFERRAL_MANAGEMENT',
      data: {
        record: selectedData[0],
      },
    };
    openModal(modalType);
  };

  createTask = () => {
    const { selectedData, openModal } = this.props;
    if (selectedData.length !== 1) return;

    const record = selectedData[0];
    const modalType = {
      type: 'CREATE_TASK',
      data: buildCreateTaskModalData(record, {
        receiverType: 'uploaded_record', // Use parent type here
        receivedAt: record.upload_date,
        callerName: null, // Uploaded records don't have caller info
      }),
    };
    openModal(modalType);
  };

  render() {
    const { selectedData } = this.props;
    // To edit, selectedData array should have a single record
    // Also, can_edit_patient_info of that record must be true.
    const canEdit = (selectedData.length === 1 && (selectedData[0].can_edit_patient_info));
    const {
      defaultFaxNumber,
      associatedFaxNumbers,
      userCanSendFaxRecordsOnly,
      autoUploadEmr,
    } = this.props;
    const viewingArchivedRecords = window.location.search.includes('archived');

    const selectedTokens = selectedData.map((record) => record.file_token);
    const canCreateTask = (
      !this.state.hideTaskButton
      && this.state.taskCreation
      && canUseStandaloneTask({
        hasEmrIntegrationCredential: this.props.hasEmrIntegrationCredential,
        credentialSupportsStandaloneTask: this.state.credentialSupportsStandaloneTask,
        autoUploadEmr: this.props.autoUploadEmr,
      })
    );

    return (
      <div className="records-redesign uploaded-records-redesign">
        <Helmet>
          <title>Uploaded Records - Medsender</title>
        </Helmet>
        <UploadedActionBar
          selectedData={selectedData}
          viewingArchivedRecords={viewingArchivedRecords}
          canEdit={canEdit}
          maxBulkSendRecords={MAX_BULK_SEND_RECORDS}
          autoUploadToEMR={this.state.autoUploadToEMR}
          createPatientChart={this.state.createPatientChart}
          canCreateTask={canCreateTask}
          referralManagement={this.props.referralManagement}
          autoUploadEmr={autoUploadEmr}
          onUpload={this.openUploadModal}
          onSend={this.checkPatientDetails}
          onEdit={this.editPatientOpen}
          onSendToChart={this.autoUploadRecord}
          onCreateChart={this.newChart}
          onCreateTask={this.createTask}
          onMoveToReferralManagement={this.moveToReferralManagement}
          onMerge={() => this.handleMergeRecords(selectedTokens)}
          onMergeAndArchive={() => this.handleMergeAndArchiveRecords(selectedTokens)}
          onArchive={() => this.archiveRecord(selectedTokens)}
          onUnarchive={() => this.unarchiveRecordOpen(selectedTokens)}
          onCreateCopy={this.cloneSelectedRecord}
        />
        <UploadedFilterBar
          paramTools={this.props.paramTools}
          getPageDataWithQuery={this.props.getPageDataWithQuery}
          location={this.props.location}
        />
        {this.state.sendDialogOpen
          && (
          <SendRecordsDialog
            faxSending={this.props.faxSending}
            handleSubmit={this.handleSendDialogSubmit}
            handleCancel={this.sendDialogVisible}
            open={this.state.sendDialogOpen}
            searchRecipient={this.props.searchRecipient}
            dataSource={this.props.dataSource}
            searchLoading={this.props.isLoading}
            patientName={this.props.selectedData.length === 1 ? this.props.selectedData[0].patient_name : ''}
            patientFirstName={this.props.selectedData.length === 1 ? this.props.selectedData[0].patient_first_name : ''}
            patientMiddleName={this.props.selectedData.length === 1 ? this.props.selectedData[0].patient_middle_name : ''}
            patientLastName={this.props.selectedData.length === 1 ? this.props.selectedData[0].patient_last_name : ''}
            patientDOB={this.props.selectedData.length === 1 ? this.props.selectedData[0].patient_dob : ''}
            selectedRecord={this.props.selectedData[0]}
            selectedRecords={this.props.selectedData}
            defaultFaxNumber={defaultFaxNumber}
            associatedFaxNumbers={associatedFaxNumbers}
            userCanSendFaxRecordsOnly={userCanSendFaxRecordsOnly}
            currentOrganizationId={this.props.currentOrganizationId}
          />
          )}
        {
          this.props.loadingDialogOpen
            ? (
              <LoadingDialog
                loading={this.props.faxSending}
                error={this.state.errorMessage}
                cancel={this.props.closeSendDialog}
              />
            ) : null
        }
      </div>
    );
  }
}

const passedProps = {
  getSelectedPageData: getUploadedRecordsByPage,
  getPageDataWithQuery: getUploadedByQuery,
  data: 'uploaded_data',
  mode: 'Uploaded',
  enableSelectAll: true,
  hideSidebar: true,
  columnTitles: uploadedColumns,
};

export default RecordsHOC(passedProps)(
  // Connect is needed here again so that we can pass and stay
  // subscribed to errors and state for both the header and dialogs
  connect(state => ({
    faxSending: state.faxes.isLoading,
    faxErrorMessage: state.faxes.errorMessage,
    faxError: state.faxes.error,
    bulkSendResponse: state.faxes.bulkSendResponse,
    selectedData: state.inboxTable.recordData,
    loadingDialogOpen: state.faxes.sendingDialogVisible,
    fileToken: state.records.fileToken,
    dataSource: state.search.results,
    isLoading: state.search.isLoading,
    associatedFaxNumbers: state.auth.data.associatedFaxNumbers,
    defaultFaxNumber: state.auth.data.defaultFaxNumber,
    userCanSendFaxRecordsOnly: state.auth.userCanSendFaxRecordsOnly,
    archiveErrorMessage: state.records.archiveErrorMessage,
    uploadedRecords: state.records.uploaded_data,
    documentTitle: state.records.documentTitle,
    documentCategory: state.records.documentCategory,
    documentCategories: state.records.documentCategories,
    currentOrganizationId: state.auth.currentOrganizationId,
    autoUploadEmr: state.auth.data.autoUploadEmr,
    hasEmrIntegrationCredential: state.auth.hasEmrIntegrationCredential,
    referralManagement: state.auth.referralManagement,
    error: state.records.error,
  }), {
    sendPatientRecord,
    sendToMultipleRecipients,
    sendBulkRecords,
    closeSendDialog,
    searchRecipient,
    openModal,
    mergeRecords,
    duplicateRecord,
    archiveRecords,
    unarchiveRecords,
    sendToDistributionList,
    triggerAutoUpload,
  })(UploadedRecords),
);
